/**
 * Servicio para gestión de inventario
 * Maneja el inventario actual y su historial completo
 *
 * NOTA: Este servicio usa el patrón Repository via DatabaseFactory para
 * TODAS las operaciones. Es compatible con web (Dexie) y mobile (SQLite).
 */

import { contraparteConValor } from '../contraparte/contraparteSearch';
import type { ContraparteCampos } from '../../types/administracion';
import {
  Stock,
  StockHistorico,
  CompraStockDTO,
  ConsumoStockDTO,
  OperacionStockResult,
  Concepto
} from '../../types/administracion';
import { getRepositories } from '../storage';
import { kgDeCantidad, unidadesDesdeKg } from '../../helpers/conceptosHelper';

/**
 * Servicio de Inventario
 *
 * Usa el repositorio via factory para operaciones de datos.
 * Compatible con web (Dexie) y mobile (SQLite).
 */
export class InventarioService {
  // ============================================================================
  // Helpers para obtener repositorios
  // ============================================================================

  private static getStockRepo() {
    const { stock } = getRepositories();
    return stock;
  }

  private static getStockHistoricoRepo() {
    const { stockHistorico } = getRepositories();
    return stockHistorico;
  }

  private static getProfileRepo() {
    const { profile } = getRepositories();
    return profile;
  }

  private static getConceptoRepo() {
    const { concepto } = getRepositories();
    return concepto;
  }

  /**
   * Convierte una cantidad entre unidades de un mismo concepto usando kg como
   * puente (unidad_carga ↔ kg, spec §3.7). Devuelve null si no hay factor de
   * conversión conocido entre las unidades.
   */
  private static convertirEntreUnidades(
    cantidad: number,
    unidadOrigen: string,
    unidadDestino: string,
    concepto: Concepto | undefined
  ): number | null {
    if (unidadOrigen === unidadDestino) return cantidad;

    const tieneFactor = !!concepto?.unidad_carga
      && concepto.unidad_carga !== 'kg'
      && !!concepto.kg_por_unidad;
    if (!tieneFactor) return null;

    const unidadCarga = concepto!.unidad_carga!;

    // Pasar la cantidad a kg (unidad base)
    let kg: number;
    if (unidadOrigen === unidadCarga) {
      kg = kgDeCantidad(cantidad, concepto!);
    } else if (unidadOrigen === 'kg') {
      kg = cantidad;
    } else {
      return null;
    }

    // De kg a la unidad destino
    if (unidadDestino === 'kg') return kg;
    if (unidadDestino === unidadCarga) return unidadesDesdeKg(kg, concepto!);
    return null;
  }

  // ============================================================================
  // CRUD de Inventario (Versión Actual)
  // ============================================================================

  /**
   * Obtiene todos los stocks de la empresa activa
   */
  static async getAll(): Promise<Stock[]> {
    try {
      return await this.getStockRepo().getAllForActiveEmpresa();
    } catch (error) {
      console.error('InventarioService.getAll error:', error);
      throw error;
    }
  }

  /**
   * Obtiene un stock por ID
   */
  static async getById(id: string): Promise<Stock | undefined> {
    try {
      return await this.getStockRepo().get(id);
    } catch (error) {
      console.error('InventarioService.getById error:', error);
      throw error;
    }
  }

  /**
   * Busca stocks por nombre (búsqueda por prefijo para autocomplete)
   */
  static async searchByName(query: string): Promise<Stock[]> {
    return await this.getStockRepo().searchByNombre(query);
  }

  /**
   * Obtiene stocks disponibles (con cantidad > 0)
   */
  static async obtenerStocksDisponibles(): Promise<Stock[]> {
    return await this.getStockRepo().getDisponibles();
  }

  /**
   * Crea un Ingresar Inventario
   * Nota: Normalmente se crea a través de registrarCompra()
   */
  static async create(stock: Partial<Stock>): Promise<string> {
    try {
      const fechaActual = new Date().toISOString();
      const nuevoStock: Omit<Stock, 'id'> = {
        nombre: stock.nombre!,
        categoria: stock.categoria!,
        unidad_medida: stock.unidad_medida!,
        cantidad: stock.cantidad || 0,
        updated_at: fechaActual,
        fecha_evento: fechaActual,
        fecha_creacion: fechaActual,
        empresa_id: stock.empresa_id!
      };

      const id = await this.getStockRepo().add(nuevoStock);

      return id;
    } catch (error) {
      console.error('Error creando stock:', error);
      throw error;
    }
  }

  /**
   * Registra una compra de stock (Ingreso)
   * Usa repositorios via factory - compatible con web y mobile.
   */
  static async registrarCompra(compra: CompraStockDTO & ContraparteCampos & {
    empresaId?: string;
    userId?: string;
    concepto_id?: string;
  }): Promise<OperacionStockResult> {
    try {
      // Get empresa_id and user_id from profiles if not provided
      let empresaId = compra.empresaId;
      let userId = compra.userId;

      if (!empresaId || !userId) {
        const userInfo = await this.getProfileRepo().getCurrentUserInfo();
        empresaId = empresaId || userInfo.empresaId;
        userId = userId || userInfo.userId;
      }

      if (!empresaId) {
        throw new Error('No active empresa found');
      }

      let stockId = '';
      const fechaActual = new Date().toISOString();

      // 1. Buscar si ya existe el stock (por nombre y categoría, dentro de la
      // empresa activa: el caché local guarda stocks de todas las empresas)
      const existingStock = await this.getStockRepo().findByNombreAndCategoria(
        compra.nombre, compra.categoria, empresaId
      );

      let cantidadResultante: number;
      // El histórico se registra en la unidad del stock resultante: si hubo
      // conversión, la operación queda en esa unidad para que cantidad_resultante,
      // el FIFO y las validaciones por replay sean coherentes entre sí.
      let cantidadOperacion = compra.cantidad;
      let unidadHistorico = compra.unidad_medida;

      if (existingStock) {
        // Actualizar existente
        stockId = existingStock.id;

        // Antes de acumular, verificar que las unidades sean compatibles: si la
        // compra llega en otra unidad (ej. rollos) y el stock está en la base
        // (kg), convertir usando el factor del concepto; sin factor, error.
        let cantidadASumar = compra.cantidad;
        if (existingStock.unidad_medida !== compra.unidad_medida) {
          const concepto = compra.concepto_id
            ? await this.getConceptoRepo().get(compra.concepto_id)
            : undefined;

          const convertida = this.convertirEntreUnidades(
            compra.cantidad,
            compra.unidad_medida,
            existingStock.unidad_medida,
            concepto
          );

          if (convertida === null) {
            throw new Error(
              `No se puede sumar ${compra.unidad_medida} al stock existente en ${existingStock.unidad_medida}. ` +
              'Convertí la cantidad o creá un stock nuevo.'
            );
          }

          cantidadASumar = convertida;
          cantidadOperacion = convertida;
          unidadHistorico = existingStock.unidad_medida;
        }

        cantidadResultante = existingStock.cantidad + cantidadASumar;

        await this.getStockRepo().update({
          id: stockId,
          cantidad: cantidadResultante,
          updated_at: fechaActual,
        });
      } else {
        // Nuevo Inventario
        stockId = await this.getStockRepo().add({
          nombre: compra.nombre,
          categoria: compra.categoria,
          concepto_id: compra.concepto_id ?? undefined,
          unidad_medida: compra.unidad_medida,
          cantidad: compra.cantidad,
          updated_at: fechaActual,
          fecha_evento: fechaActual,
          fecha_creacion: fechaActual,
          empresa_id: empresaId
        });
        cantidadResultante = compra.cantidad;
      }

      // 2. Registrar en Histórico
      await this.getStockHistoricoRepo().add({
        stock_id: stockId,
        fecha_evento: compra.fecha_compra,
        updated_at: fechaActual,
        tipo_operacion: 'COMPRA',
        cantidad_operacion: cantidadOperacion,
        cantidad_resultante: cantidadResultante,
        precio_total: compra.cantidad * compra.precio_unitario,
        nombre: compra.nombre,
        categoria: compra.categoria,
        concepto_id: compra.concepto_id ?? undefined,
        unidad_medida: unidadHistorico,
        // Proveedor (2026-10-18): opcional, sólo viaja con valor.
        ...contraparteConValor(compra),
        empresa_id: empresaId,
        user_id: userId!
      });

      return {
        success: true,
        stock_id: stockId,
        historico_id: 'generated_in_tx',
        message: `Compra registrada: ${compra.cantidad} ${compra.unidad_medida} de ${compra.nombre}`
      };
    } catch (error) {
      console.error('Error registrando compra:', error);
      throw error;
    }
  }

  /**
   * Calcula el costo FIFO para un consumo de stock.
   *
   * Sin `cutoff`, costea contra los lotes remanentes FINALES (valuación del
   * inventario actual / validación de un consumo aún no registrado).
   * Con `cutoff = { id }`, replaya el historial sólo hasta esa operación
   * (exclusive): así se costea una VENTA ya registrada contra los lotes
   * vigentes al momento de esa venta, sin lookahead (mismo criterio que
   * getPrecioTotalConsumoOnDemand para consumos).
   */
  static async calcularCostoFIFO(
    stockId: string,
    cantidadConsumida: number,
    cutoff?: { id: string }
  ): Promise<number> {
    try {
      // Obtener todo el historial del stock
      const historialCompleto = await this.getStockHistoricoRepo().getByStockId(stockId);

      // Ordenar por fecha_evento primero, luego por updated_at ascendente
      historialCompleto.sort((a, b) => {
        const fechaA = a.fecha_evento;
        const fechaB = b.fecha_evento;
        if (fechaA < fechaB) return -1;
        if (fechaA > fechaB) return 1;
        return (a.updated_at || '').localeCompare(b.updated_at || '');
      });

      // Calcular el stock disponible por lote (compra) considerando FIFO
      const lotesDisponibles: Array<{
        fecha: string;
        cantidadDisponible: number;
        precioUnitario: number;
      }> = [];

      // Procesar todas las transacciones para determinar stock disponible por lote
      for (const transaccion of historialCompleto) {
        // Costeo de una operación histórica: el replay corta en ella, para no
        // consumir lotes posteriores a la fecha que se está costeando.
        if (cutoff && transaccion.id === cutoff.id) break;

        if (transaccion.tipo_operacion === 'COMPRA') {
          lotesDisponibles.push({
            fecha: transaccion.fecha_evento,
            cantidadDisponible: transaccion.cantidad_operacion,
            precioUnitario: this.getPrecioUnitarioOnDemand(transaccion)
          });
        } else if (transaccion.tipo_operacion === 'CONSUMO' || transaccion.tipo_operacion === 'VENTA') {
          let cantidadPorConsumir = Math.abs(transaccion.cantidad_operacion);

          for (let i = 0; i < lotesDisponibles.length && cantidadPorConsumir > 0; i++) {
            const lote = lotesDisponibles[i];
            if (lote.cantidadDisponible > 0) {
              const cantidadAConsumirDelLote = Math.min(cantidadPorConsumir, lote.cantidadDisponible);
              lote.cantidadDisponible -= cantidadAConsumirDelLote;
              cantidadPorConsumir -= cantidadAConsumirDelLote;
            }
          }
        }
      }

      // Ahora consumir la cantidad solicitada usando FIFO
      let cantidadRestante = cantidadConsumida;
      let costoTotal = 0;

      for (const lote of lotesDisponibles) {
        if (cantidadRestante <= 0) break;

        if (lote.cantidadDisponible > 0) {
          const cantidadAConsumir = Math.min(cantidadRestante, lote.cantidadDisponible);
          costoTotal += cantidadAConsumir * lote.precioUnitario;
          cantidadRestante -= cantidadAConsumir;
        }
      }

      if (cantidadRestante > 0) {
        throw new Error(`Inventario insuficiente para consumo FIFO. Faltan: ${cantidadRestante}`);
      }

      return costoTotal;
    } catch (error) {
      console.error('Error calculando costo FIFO:', error);
      throw error;
    }
  }

  /**
   * Validates if there's enough stock available on a specific date using FIFO
   */
  static async validateStockAvailability(
    stockId: string,
    cantidad: number,
    fecha: string
  ): Promise<{ isValid: boolean; errorMessage?: string }> {
    try {
      const historialHastaFecha = await this.getStockHistoricoRepo().getByStockIdAndFecha(stockId, fecha);

      // Sort by fecha_evento ascending
      historialHastaFecha.sort((a, b) => {
        const fechaA = a.fecha_evento;
        const fechaB = b.fecha_evento;
        if (fechaA < fechaB) return -1;
        if (fechaA > fechaB) return 1;
        return (a.updated_at || '').localeCompare(b.updated_at || '');
      });

      // Calculate available stock using FIFO
      let stockDisponible = 0;

      for (const operacion of historialHastaFecha) {
        if (operacion.tipo_operacion === 'COMPRA') {
          stockDisponible += operacion.cantidad_operacion;
        } else if (operacion.tipo_operacion === 'CONSUMO' || operacion.tipo_operacion === 'VENTA') {
          stockDisponible -= Math.abs(operacion.cantidad_operacion);
        }
      }

      if (stockDisponible < cantidad) {
        return {
          isValid: false,
          errorMessage: `No hay suficiente inventario disponible para esta cantidad en la fecha seleccionada (${new Date(fecha).toLocaleDateString()}). Inventario disponible: ${stockDisponible}, requerido: ${cantidad}`
        };
      }

      return { isValid: true };
    } catch (error) {
      console.error('Error validating stock availability:', error);
      return {
        isValid: false,
        errorMessage: 'Error al validar la disponibilidad de inventario.'
      };
    }
  }

  /**
   * Registra un consumo de stock
   * Usa repositorios via factory - compatible con web y mobile.
   */
  static async registrarConsumo(consumo: ConsumoStockDTO & {
    empresaId?: string;
    userId?: string;
  }): Promise<OperacionStockResult> {
    try {
      let empresaId = consumo.empresaId;
      let userId = consumo.userId;

      if (!empresaId || !userId) {
        const userInfo = await this.getProfileRepo().getCurrentUserInfo();
        empresaId = empresaId || userInfo.empresaId;
        userId = userId || userInfo.userId;
      }

      if (!empresaId) {
        throw new Error('No active empresa found');
      }

      const stock = await this.getById(consumo.stock_id);
      if (!stock) throw new Error('Producto de inventario no encontrado');
      if (stock.cantidad < consumo.cantidad) {
        throw new Error(`Inventario insuficiente. Disponible: ${stock.cantidad}, Solicitado: ${consumo.cantidad}`);
      }

      // Calcular costo FIFO
      await this.calcularCostoFIFO(stock.id, consumo.cantidad);

      const fechaActual = new Date().toISOString();
      const newCantidad = stock.cantidad - consumo.cantidad;

      await this.getStockRepo().update({
        id: stock.id,
        cantidad: newCantidad,
        updated_at: fechaActual,
      });

      await this.getStockHistoricoRepo().add({
        stock_id: stock.id,
        fecha_evento: consumo.fecha_consumo,
        updated_at: fechaActual,
        tipo_operacion: 'CONSUMO',
        cantidad_operacion: -consumo.cantidad,
        cantidad_resultante: newCantidad,
        precio_total: 0,
        nombre: stock.nombre,
        categoria: stock.categoria,
        unidad_medida: stock.unidad_medida,
        ubicacion_id: consumo.ubicacion_id,
        empresa_id: empresaId,
        user_id: userId!
      });

      return {
        success: true,
        stock_id: stock.id,
        historico_id: 'generated_in_tx',
        message: `Consumo registrado: ${consumo.cantidad} ${stock.unidad_medida} de ${stock.nombre}`
      };
    } catch (error) {
      console.error('Error registrando consumo:', error);
      throw error;
    }
  }

  /**
   * Registra una venta de stock
   * Usa repositorios via factory - compatible con web y mobile.
   */
  static async registrarVenta(venta: ConsumoStockDTO & ContraparteCampos & {
    precio_venta: number;
    empresaId?: string;
    userId?: string;
  }): Promise<OperacionStockResult> {
    try {
      let empresaId = venta.empresaId;
      let userId = venta.userId;

      if (!empresaId || !userId) {
        const userInfo = await this.getProfileRepo().getCurrentUserInfo();
        empresaId = empresaId || userInfo.empresaId;
        userId = userId || userInfo.userId;
      }

      if (!empresaId) {
        throw new Error('No active empresa found');
      }

      const stock = await this.getById(venta.stock_id);
      if (!stock) throw new Error('Producto de inventario no encontrado');
      if (stock.cantidad < venta.cantidad) {
        throw new Error(`Inventario insuficiente. Disponible: ${stock.cantidad}, Solicitado: ${venta.cantidad}`);
      }

      const fechaActual = new Date().toISOString();
      const newCantidad = stock.cantidad - venta.cantidad;

      await this.getStockRepo().update({
        id: stock.id,
        cantidad: newCantidad,
        updated_at: fechaActual,
      });

      await this.getStockHistoricoRepo().add({
        stock_id: stock.id,
        fecha_evento: venta.fecha_consumo,
        updated_at: fechaActual,
        tipo_operacion: 'VENTA',
        cantidad_operacion: -venta.cantidad,
        cantidad_resultante: newCantidad,
        precio_total: venta.cantidad * venta.precio_venta,
        nombre: stock.nombre,
        categoria: stock.categoria,
        unidad_medida: stock.unidad_medida,
        ubicacion_id: venta.ubicacion_id,
        // Comprador (2026-10-18): opcional, sólo viaja con valor.
        ...contraparteConValor(venta),
        empresa_id: empresaId,
        user_id: userId!
      });

      return {
        success: true,
        stock_id: stock.id,
        historico_id: 'generated_in_tx',
        message: `Venta registrada: ${venta.cantidad} ${stock.unidad_medida} de ${stock.nombre}`
      };
    } catch (error) {
      console.error('Error registrando venta:', error);
      throw error;
    }
  }

  // ============================================================================
  // Histórico de Inventario
  // ============================================================================

  static async getAllHistorico(): Promise<StockHistorico[]> {
    try {
      return await this.getStockHistoricoRepo().getAllForActiveEmpresa();
    } catch (error) {
      console.error('InventarioService.getAllHistorico error:', error);
      throw error;
    }
  }

  static async getHistoricoByStock(stockId: string): Promise<StockHistorico[]> {
    try {
      const historico = await this.getStockHistoricoRepo().getByStockId(stockId);

      // Sort by fecha_evento ascending, then by updated_at ascending
      historico.sort((a, b) => {
        const fechaA = a.fecha_evento;
        const fechaB = b.fecha_evento;
        if (fechaA < fechaB) return -1;
        if (fechaA > fechaB) return 1;
        return (a.updated_at || '').localeCompare(b.updated_at || '');
      });

      // Reverse for display (newest first)
      return historico.reverse();
    } catch (error) {
      console.error('InventarioService.getHistoricoByStock error:', error);
      throw error;
    }
  }

  static async getHistoricoByPeriodo(
    fechaInicio: string,
    fechaFin: string
  ): Promise<StockHistorico[]> {
    try {
      return await this.getStockHistoricoRepo().getByPeriodo(fechaInicio, fechaFin);
    } catch (error) {
      console.error('InventarioService.getHistoricoByPeriodo error:', error);
      throw error;
    }
  }

  // ============================================================================
  // Análisis y Reportes
  // ============================================================================

  static async getValorTotalInventario(): Promise<{ pesos: number }> {
    const stocks = await this.getAll();
    let totalPesos = 0;

    for (const stock of stocks) {
      if (stock.cantidad <= 0) continue;
      if (stock.categoria === 'Hacienda') continue;

      try {
        const valorInventario = await this.calcularCostoFIFO(stock.id, stock.cantidad);
        totalPesos += valorInventario;
      } catch (error) {
        console.warn(`No se pudo calcular el valor FIFO para stock ${stock.id}:`, error);
        const historial = await this.getHistoricoByStock(stock.id);
        const compras = historial.filter(h => h.tipo_operacion === 'COMPRA');
        if (compras.length > 0) {
          const precioUnitarioPromedio = compras.reduce((sum, compra) =>
            sum + (compra.precio_total / Math.abs(compra.cantidad_operacion)), 0) / compras.length;
          totalPesos += stock.cantidad * precioUnitarioPromedio;
        }
      }
    }

    return { pesos: totalPesos };
  }

  static async getEstadisticasPorCategoria(): Promise<Map<string, {
    cantidad_productos: number;
    valor_total: number;
    cantidad_total: number;
  }>> {
    const stocks = await this.getAll();
    const stats = new Map();

    for (const stock of stocks) {
      const actual = stats.get(stock.categoria) || {
        cantidad_productos: 0,
        valor_total: 0,
        cantidad_total: 0
      };

      const historial = await this.getHistoricoByStock(stock.id);
      const compras = historial.filter(h => h.tipo_operacion === 'COMPRA');
      let precioUnitarioPromedio = 0;
      if (compras.length > 0) {
        precioUnitarioPromedio = compras.reduce((sum, compra) =>
          sum + (compra.precio_total / Math.abs(compra.cantidad_operacion)), 0) / compras.length;
      }

      stats.set(stock.categoria, {
        cantidad_productos: actual.cantidad_productos + 1,
        valor_total: actual.valor_total + (stock.cantidad * precioUnitarioPromedio),
        cantidad_total: actual.cantidad_total + stock.cantidad
      });
    }

    return stats;
  }

  /**
   * Valida que eliminar una compra no rompa la integridad histórica
   */
  static async validarEliminacionCompra(compraId: string): Promise<string | null> {
    const compra = await this.getStockHistoricoRepo().get(compraId);
    if (!compra || compra.tipo_operacion !== 'COMPRA') {
      throw new Error('Operación no es una compra válida');
    }

    const operacionesPosteriores = await this.getStockHistoricoRepo().getOperacionesPosteriores(
      compra.stock_id,
      compra.fecha_evento,
      compra.updated_at || ''
    );

    // Simular el estado del inventario sin esta compra
    let stockSimulado = 0;

    const operacionesAnteriores = await this.getStockHistoricoRepo().getOperacionesAnteriores(
      compra.stock_id,
      compra.fecha_evento,
      compra.updated_at || ''
    );

    for (const operacion of operacionesAnteriores) {
      if (operacion.id === compra.id) continue;

      if (operacion.tipo_operacion === 'COMPRA') {
        stockSimulado += operacion.cantidad_operacion;
      } else if (operacion.tipo_operacion === 'CONSUMO' || operacion.tipo_operacion === 'VENTA') {
        stockSimulado -= Math.abs(operacion.cantidad_operacion);
      }
    }

    for (const operacion of operacionesPosteriores) {
      if (operacion.tipo_operacion === 'COMPRA') {
        stockSimulado += operacion.cantidad_operacion;
      } else if (operacion.tipo_operacion === 'CONSUMO' || operacion.tipo_operacion === 'VENTA') {
        if (stockSimulado < Math.abs(operacion.cantidad_operacion)) {
          return `No se puede eliminar esta compra porque la operación "${operacion.tipo_operacion}" ` +
            `(${new Date(operacion.fecha_evento).toLocaleDateString()}) quedaría sin suficiente inventario histórico. ` +
            `Inventario disponible: ${stockSimulado}, requerido: ${Math.abs(operacion.cantidad_operacion)}`;
        }
        stockSimulado -= Math.abs(operacion.cantidad_operacion);
      }
    }

    return null;
  }

  /**
   * Elimina una operación histórica de stock y revierte sus efectos
   * Usa repositorios via factory - compatible con web y mobile.
   */
  static async deleteHistoricoOperation(historicoId: string): Promise<string | null> {
    try {
      const historico = await this.getStockHistoricoRepo().get(historicoId);
      if (!historico) {
        throw new Error('Operación histórica no encontrada');
      }

      const stock = await this.getById(historico.stock_id);
      if (!stock) {
        throw new Error('Producto de inventario no encontrado');
      }

      const fechaActual = new Date().toISOString();

      if (historico.tipo_operacion === 'COMPRA') {
        const errorMessage = await this.validarEliminacionCompra(historicoId);
        if (errorMessage) {
          return errorMessage;
        }
      }

      let newCantidad = stock.cantidad;

      if (historico.tipo_operacion === 'COMPRA') {
        if (stock.cantidad < historico.cantidad_operacion) {
          return 'No se puede eliminar esta compra porque reduciría el inventario por debajo de cero';
        }
        newCantidad -= historico.cantidad_operacion;
      } else if (historico.tipo_operacion === 'CONSUMO' || historico.tipo_operacion === 'VENTA') {
        newCantidad += Math.abs(historico.cantidad_operacion);
      }

      await this.getStockRepo().update({
        id: stock.id,
        cantidad: newCantidad,
        updated_at: fechaActual,
      });

      // Soft delete: marcar como eliminado
      await this.getStockHistoricoRepo().update({
        ...historico,
        is_deleted: true,
        updated_at: fechaActual,
      });

      return null;
    } catch (error) {
      console.error('Error eliminando operación histórica:', error);
      return 'Error interno al eliminar la operación. Probá de nuevo.';
    }
  }

  /**
   * Edita una operación histórica de stock
   * Usa repositorios via factory - compatible con web y mobile.
   */
  static async updateHistoricoOperation(
    historicoId: string,
    updates: Partial<Pick<StockHistorico, 'fecha_evento' | 'cantidad_operacion'>>
  ): Promise<void> {
    try {
      const historico = await this.getStockHistoricoRepo().get(historicoId);
      if (!historico) {
        throw new Error('Operación histórica no encontrada');
      }

      const stock = await this.getById(historico.stock_id);
      if (!stock) {
        throw new Error('Producto de inventario no encontrado');
      }

      const fechaActual = new Date().toISOString();

      // Revertir efecto original
      let newCantidad = stock.cantidad;
      if (historico.tipo_operacion === 'COMPRA') {
        newCantidad -= historico.cantidad_operacion;
      } else if (historico.tipo_operacion === 'CONSUMO' || historico.tipo_operacion === 'VENTA') {
        newCantidad += Math.abs(historico.cantidad_operacion);
      }

      const updatedHistorico = { ...historico };

      if (updates.fecha_evento) {
        updatedHistorico.fecha_evento = updates.fecha_evento;
      }

      if (updates.cantidad_operacion !== undefined) {
        updatedHistorico.cantidad_operacion = updates.cantidad_operacion;
        const precioUnitario = this.getPrecioUnitarioOnDemand(updatedHistorico);
        updatedHistorico.precio_total = Math.abs(updates.cantidad_operacion) * precioUnitario;
      }

      // Aplicar nuevo efecto
      if (updatedHistorico.tipo_operacion === 'COMPRA') {
        newCantidad += updatedHistorico.cantidad_operacion;
      } else if (updatedHistorico.tipo_operacion === 'CONSUMO' || updatedHistorico.tipo_operacion === 'VENTA') {
        newCantidad -= Math.abs(updatedHistorico.cantidad_operacion);
      }

      updatedHistorico.updated_at = fechaActual;

      await this.getStockRepo().update({
        id: stock.id,
        cantidad: newCantidad,
        updated_at: fechaActual,
      });

      await this.getStockHistoricoRepo().update(updatedHistorico);

    } catch (error) {
      console.error('Error actualizando operación histórica:', error);
      throw error;
    }
  }

  /**
   * Calcula el precio_unitario de cualquier operación
   */
  static getPrecioUnitarioOnDemand(historico: StockHistorico): number {
    if (historico.cantidad_operacion === 0) return 0;
    return historico.precio_total / Math.abs(historico.cantidad_operacion);
  }

  /**
   * Calcula el precio_total de un consumo específico usando FIFO on-demand
   */
  static async getPrecioTotalConsumoOnDemand(historicoId: string): Promise<number> {
    try {
      const consumo = await this.getStockHistoricoRepo().get(historicoId);
      if (!consumo || consumo.tipo_operacion !== 'CONSUMO') {
        throw new Error('Registro de consumo no encontrado');
      }

      if (consumo.precio_total > 0) {
        return consumo.precio_total;
      }

      const historialHastaConsumo = await this.getStockHistoricoRepo().getByStockIdAndFecha(
        consumo.stock_id,
        consumo.fecha_evento
      );

      historialHastaConsumo.sort((a, b) => {
        const fechaA = a.fecha_evento;
        const fechaB = b.fecha_evento;
        if (fechaA < fechaB) return -1;
        if (fechaA > fechaB) return 1;
        return (a.updated_at || '').localeCompare(b.updated_at || '');
      });

      const lotesDisponibles: Array<{
        fecha: string;
        cantidadDisponible: number;
        precioUnitario: number;
      }> = [];

      for (const operacion of historialHastaConsumo) {
        if (operacion.id === consumo.id) break;

        if (operacion.tipo_operacion === 'COMPRA') {
          lotesDisponibles.push({
            fecha: operacion.fecha_evento,
            cantidadDisponible: operacion.cantidad_operacion,
            precioUnitario: this.getPrecioUnitarioOnDemand(operacion)
          });
        } else if (operacion.tipo_operacion === 'CONSUMO' || operacion.tipo_operacion === 'VENTA') {
          let cantidadPorConsumir = Math.abs(operacion.cantidad_operacion);

          for (const lote of lotesDisponibles) {
            if (cantidadPorConsumir <= 0) break;
            if (lote.cantidadDisponible > 0) {
              const cantidadAConsumir = Math.min(cantidadPorConsumir, lote.cantidadDisponible);
              lote.cantidadDisponible -= cantidadAConsumir;
              cantidadPorConsumir -= cantidadAConsumir;
            }
          }
        }
      }

      const cantidadConsumida = Math.abs(consumo.cantidad_operacion);
      let cantidadRestante = cantidadConsumida;
      let costoTotal = 0;

      for (const lote of lotesDisponibles) {
        if (cantidadRestante <= 0) break;
        if (lote.cantidadDisponible > 0) {
          const cantidadAConsumir = Math.min(cantidadRestante, lote.cantidadDisponible);
          costoTotal += cantidadAConsumir * lote.precioUnitario;
          cantidadRestante -= cantidadAConsumir;
        }
      }

      if (cantidadRestante > 0) {
        throw new Error('No hay suficiente inventario histórico para calcular el precio');
      }

      return costoTotal;
    } catch (error) {
      console.error('Error calculando precio_total on-demand:', error);
      throw error;
    }
  }

  /**
   * Calcula el precio_unitario de un consumo específico usando FIFO on-demand
   */
  static async getPrecioUnitarioConsumoOnDemand(historicoId: string): Promise<number> {
    const precioTotal = await this.getPrecioTotalConsumoOnDemand(historicoId);
    const consumo = await this.getStockHistoricoRepo().get(historicoId);

    if (!consumo) return 0;
    return precioTotal / Math.abs(consumo.cantidad_operacion);
  }

  /**
   * Igual que getPrecioTotalConsumoOnDemand pero devuelve el DESGLACE por lote
   * (cada lote = { fecha de la COMPRA, costo de la porción consumida de ese lote }).
   *
   * Se usa en costos históricos para poder reactualizar cada porción al USD de
   * la fecha de COMPRA (no de consumo), como pide el usuario.
   */
  static async getFifoDetalleConsumo(
    historicoId: string
  ): Promise<Array<{ fecha: string; costo: number }>> {
    try {
      const consumo = await this.getStockHistoricoRepo().get(historicoId);
      if (!consumo || consumo.tipo_operacion !== 'CONSUMO') return [];

      // Si ya tiene precio_total > 0 (no FIFO necesario), usarlo con la fecha del consumo.
      if (consumo.precio_total > 0) {
        return [{ fecha: consumo.fecha_evento, costo: consumo.precio_total }];
      }

      const historial = await this.getStockHistoricoRepo().getByStockIdAndFecha(
        consumo.stock_id,
        consumo.fecha_evento
      );
      historial.sort((a, b) => {
        if (a.fecha_evento < b.fecha_evento) return -1;
        if (a.fecha_evento > b.fecha_evento) return 1;
        return (a.updated_at || '').localeCompare(b.updated_at || '');
      });

      // Reconstruir lotes disponibles (FIFO) hasta el momento del consumo.
      const lotes: Array<{ fecha: string; cantidadDisponible: number; precioUnitario: number }> = [];
      for (const op of historial) {
        if (op.id === consumo.id) break;
        if (op.tipo_operacion === 'COMPRA') {
          lotes.push({
            fecha: op.fecha_evento,
            cantidadDisponible: op.cantidad_operacion,
            precioUnitario: this.getPrecioUnitarioOnDemand(op),
          });
        } else if (op.tipo_operacion === 'CONSUMO' || op.tipo_operacion === 'VENTA') {
          let porConsumir = Math.abs(op.cantidad_operacion);
          for (const l of lotes) {
            if (porConsumir <= 0) break;
            const c = Math.min(porConsumir, l.cantidadDisponible);
            l.cantidadDisponible -= c;
            porConsumir -= c;
          }
        }
      }

      // Consumir la cantidad solicitada de los lotes disponibles (FIFO).
      const cantidad = Math.abs(consumo.cantidad_operacion);
      let restante = cantidad;
      const detalle: Array<{ fecha: string; costo: number }> = [];
      for (const l of lotes) {
        if (restante <= 0) break;
        if (l.cantidadDisponible > 0) {
          const c = Math.min(restante, l.cantidadDisponible);
          detalle.push({ fecha: l.fecha, costo: c * l.precioUnitario });
          restante -= c;
        }
      }
      return detalle;
    } catch (error) {
      console.error('Error en getFifoDetalleConsumo:', error);
      return [];
    }
  }

  /**
   * Calcula los meses restantes de stock basado en consumo promedio histórico
   */
  static async getStockConsumptionAnalysis(currentDate?: Date): Promise<Array<{
    stock: Stock;
    monthsRemaining: number | null;
    averageMonthlyConsumption: number;
    totalHistoricalConsumption: number;
    monthsSinceFirstRegister: number;
  }>> {
    const stocks = await this.getAll();
    const now = currentDate || new Date();
    const results = [];

    for (const stock of stocks) {
      try {
        const historico = await this.getHistoricoByStock(stock.id);

        if (historico.length === 0) {
          results.push({
            stock,
            monthsRemaining: null,
            averageMonthlyConsumption: 0,
            totalHistoricalConsumption: 0,
            monthsSinceFirstRegister: 0
          });
          continue;
        }

        const firstRegisterDate = new Date(Math.min(...historico.map(h => new Date(h.fecha_evento).getTime())));
        const monthsSinceFirstRegister = (now.getTime() - firstRegisterDate.getTime()) / (1000 * 60 * 60 * 24 * 30.44);

        const monthlyConsumption: Map<string, number> = new Map();
        historico
          .filter(h => h.tipo_operacion === 'CONSUMO')
          .forEach(h => {
            const date = new Date(h.fecha_evento);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            const current = monthlyConsumption.get(monthKey) || 0;
            monthlyConsumption.set(monthKey, current + Math.abs(h.cantidad_operacion));
          });

        const monthlyTotals = Array.from(monthlyConsumption.values());
        const numberOfMonths = monthlyTotals.length;

        if (numberOfMonths <= 0) {
          results.push({
            stock,
            monthsRemaining: null,
            averageMonthlyConsumption: 0,
            totalHistoricalConsumption: 0,
            monthsSinceFirstRegister
          });
          continue;
        }

        const averageMonthlyConsumption = monthlyTotals.reduce((sum, total) => sum + total, 0) / numberOfMonths;
        const totalHistoricalConsumption = monthlyTotals.reduce((sum, total) => sum + total, 0);
        const monthsRemaining = averageMonthlyConsumption > 0 ? stock.cantidad / averageMonthlyConsumption : null;

        results.push({
          stock,
          monthsRemaining,
          averageMonthlyConsumption,
          totalHistoricalConsumption,
          monthsSinceFirstRegister
        });

      } catch (error) {
        console.error(`Error calculating consumption analysis for stock ${stock.id}:`, error);
        results.push({
          stock,
          monthsRemaining: null,
          averageMonthlyConsumption: 0,
          totalHistoricalConsumption: 0,
          monthsSinceFirstRegister: 0
        });
      }
    }

    return results;
  }
}

export default InventarioService;
